import type { DestinationType } from "app/js/src/domain/clipping-studio/publish/publish-models";
import type { TikTokDestinationPlatformData } from "app/js/src/domain/destinations/destination-details.interfaces";
import {
  FACEBOOK_CONTENT_CATEGORIES,
  FACEBOOK_PUBLISH_TARGETS,
} from "app/js/src/domain/destinations/facebook-content-categories";
import {
  FACEBOOK_CONTENT_CATEGORY_NONE,
  isFacebookReelPublishTarget,
  type FacebookPublicationSettings,
} from "app/js/src/domain/destinations/facebook-publication-settings";
import { INSTAGRAM_POST_TYPES } from "app/js/src/domain/destinations/instagram-post-types";
import type { InstagramPublicationSettings } from "app/js/src/domain/destinations/instagram-publication-settings";
import type { YouTubePublicationSettings } from "app/js/src/domain/destinations/youtube-publication-settings";

export type PublicationSettingsSummaryInput =
  | FacebookPublicationSettings
  | InstagramPublicationSettings
  | YouTubePublicationSettings
  | TikTokDestinationPlatformData;

function labelFromCatalog(
  catalog: readonly { readonly value: string; readonly label: string }[],
  value: string | null | undefined,
): string | null {
  if (value == null || value === "") return null;
  return catalog.find((o) => o.value === value)?.label ?? value;
}

function pushIfTrue(lines: string[], enabled: boolean | null | undefined, label: string): void {
  if (enabled === true) {
    lines.push(label);
  }
}

function facebookSummaryLines(settings: FacebookPublicationSettings): string[] {
  const lines: string[] = [];
  const targetLabel = labelFromCatalog(FACEBOOK_PUBLISH_TARGETS, settings.publish_target);
  if (targetLabel) {
    lines.push(targetLabel);
  }

  if (isFacebookReelPublishTarget(settings.publish_target)) {
    return lines;
  }

  const category = settings.content_category?.trim();
  if (category && category !== FACEBOOK_CONTENT_CATEGORY_NONE) {
    const categoryLabel = labelFromCatalog(FACEBOOK_CONTENT_CATEGORIES, category);
    if (categoryLabel) {
      lines.push(categoryLabel);
    }
  }

  pushIfTrue(lines, settings.embeddable, "Embeddable");
  pushIfTrue(lines, settings.no_story, "No story");
  return lines;
}

function instagramSummaryLines(settings: InstagramPublicationSettings): string[] {
  const lines: string[] = [];
  const postTypeLabel = labelFromCatalog(INSTAGRAM_POST_TYPES, settings.default_post_type);
  if (postTypeLabel) {
    lines.push(postTypeLabel);
  }
  pushIfTrue(lines, settings.share_to_feed, "Share to feed");
  pushIfTrue(lines, settings.is_ai_generated, "AI-generated content");
  return lines;
}

function youtubeSummaryLines(
  settings: YouTubePublicationSettings,
  youtubeCategoryTitle?: string | null,
): string[] {
  const lines: string[] = [];
  const privacy = settings.privacy?.trim();
  if (privacy) {
    lines.push(privacy);
  }
  const categoryId = settings.category_id?.trim();
  if (categoryId) {
    const title = youtubeCategoryTitle?.trim();
    lines.push(title || `Category ${categoryId}`);
  }
  pushIfTrue(lines, settings.self_declared_made_for_kids, "Made for kids");
  pushIfTrue(lines, settings.notify_subscribers, "Notify subscribers");
  return lines;
}

function tiktokSummaryLines(settings: TikTokDestinationPlatformData): string[] {
  const lines: string[] = [];
  const privacy = settings.privacy_level?.trim();
  if (privacy) {
    lines.push(privacy);
  }

  // Form checkboxes are Allow*; platform stores disable_* inverted.
  if (settings.disable_comment === false) {
    lines.push("Allow Comment");
  }
  if (settings.disable_duet === false) {
    lines.push("Allow Duet");
  }
  if (settings.disable_stitch === false) {
    lines.push("Allow Stitch");
  }

  pushIfTrue(
    lines,
    settings.content_disclosure_enabled,
    "This content promotes myself, a brand, product, or service",
  );
  pushIfTrue(lines, settings.is_brand_organic, "Your Brand");
  pushIfTrue(lines, settings.is_brand_content, "Branded Content");
  pushIfTrue(lines, settings.is_aigc, "AI-generated content");
  return lines;
}

/**
 * Human-readable settings rows for a publish preview card.
 * Caller must pass already-effective settings (draft / override / tenant defaults).
 */
export function buildPublicationSettingsSummaryLines(args: {
  type: DestinationType;
  settings: PublicationSettingsSummaryInput | null;
  youtubeCategoryTitle?: string | null;
}): string[] {
  const { type, settings, youtubeCategoryTitle } = args;
  if (!settings) {
    return [];
  }

  switch (type) {
    case "fb":
      return facebookSummaryLines(settings as FacebookPublicationSettings);
    case "ig":
      return instagramSummaryLines(settings as InstagramPublicationSettings);
    case "yt":
      return youtubeSummaryLines(
        settings as YouTubePublicationSettings,
        youtubeCategoryTitle,
      );
    case "tt":
      return tiktokSummaryLines(settings as TikTokDestinationPlatformData);
    default:
      return [];
  }
}
